/** @jsxImportSource react */
import React, { FormEvent, useEffect, useState, useRef, useMemo, FC } from 'react'
import { useMutation } from '@tanstack/react-query'
import { toast } from './ui/Toast'
import { useAuth } from '../contexts/auth'
import { api } from '../services/api'
import { useTranslation } from '../i18n'
import { ChatMessage, User, UserRole } from '../types'
import { Button } from './ui/Button'
import { Send, Loader2, Paperclip, X } from 'lucide-react'
import { Card, CardContent, CardFooter } from './ui/Card'


interface ChatSectionProps {
  orderId: string
}

export const ChatSection: FC<ChatSectionProps> = ({ orderId }) => {
  const { user } = useAuth()
  const { t, formatDate } = useTranslation()
  const [messageText, setMessageText] = useState('')
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const chatContainerRef = useRef<HTMLDivElement>(null)
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [isLoadingMessages, setIsLoadingMessages] = useState(true);

  useEffect(() => {
    if (!orderId) return;

    const unsubMessages = api.listenToMessagesForOrder(orderId, (newMessages) => {
        setMessages(newMessages);
        setIsLoadingMessages(false);
    });

    let unsubUsers = () => {};
    // Restrict user listening to Admin and Staff to prevent permission_denied errors for clients
    if (user?.role === UserRole.Admin || user?.role === UserRole.Staff) {
        unsubUsers = api.listenToUsers(setUsers);
    }

    return () => {
        unsubMessages();
        unsubUsers();
    };
  }, [orderId, user?.role]);
  
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const userMap = useMemo(() => {
      return users.reduce((acc, u) => {
          acc[u.id] = u;
          return acc;
      }, {} as Record<string, User>);
  }, [users]);
  
  const addMessageMutation = useMutation({
      mutationFn: async (data: { text: string; imageFile: File | null }) => {
          let imageUrl: string | undefined;
          if (data.imageFile) {
              // Upload the file to Firebase Storage and get the URL
              imageUrl = await api.uploadFile(data.imageFile, 'chat_images');
          }
          // Add the message with the optional URL to the database
          await api.addMessageToOrder(orderId, data.text, imageUrl);
      },
      onSuccess: () => {
          setMessageText('');
          setImageFile(null);
          setImagePreview(null);
          if (fileInputRef.current) fileInputRef.current.value = '';
      },
      onError: (error: any) => {
          console.error("Chat Error:", error);
          const errorMsg = error?.message || t('sendMessageError');
          toast.error(errorMsg);
      },
  });


  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) { // 5MB limit
        toast.error(t('fileTooLargeError', { size: '5MB'}));
        return;
      }
      setImageFile(file);
      const previewUrl = URL.createObjectURL(file);
      setImagePreview(previewUrl);
    }
  };

  const removeImage = () => {
      setImageFile(null);
      setImagePreview(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const getSenderName = (senderId: string) => {
      if (userMap[senderId]) return userMap[senderId].name;
      // Fallback for clients who cannot fetch the user list
      if (user?.role === UserRole.Client && senderId !== user.id) {
          return t('supportTeam', { defaultValue: 'Support Team' });
      }
      return t('unknownSender');
  };

  const handleSubmit = async (e: FormEvent) => {
      e.preventDefault();
      if (!messageText.trim() && !imageFile) return;

      addMessageMutation.mutate({ text: messageText, imageFile: imageFile });
  };
  
  return (
      <Card>
          <CardContent ref={chatContainerRef} className="h-96 overflow-y-auto p-4 space-y-4 bg-secondary-50">
              {isLoadingMessages ? (
                  <div className="flex justify-center items-center h-full">
                      <Loader2 className="animate-spin text-primary-500" size={32} />
                  </div>
              ) : messages.length === 0 ? (
                  <div className="flex justify-center items-center h-full text-secondary-500">
                      {t('no_messages_yet')}
                  </div>
              ) : messages.map((msg) => {
                  const isCurrentUser = msg.senderId === user?.id;
                  return (
                      <div key={msg.id} className="flex">
                          <div className={`flex items-end gap-2 ${isCurrentUser ? 'ml-auto' : 'mr-auto rtl:flex-row-reverse'}`}>
                              {!isCurrentUser && (
                                  <div className="w-8 h-8 rounded-full bg-primary-100 text-primary-600 flex items-center justify-center font-bold flex-shrink-0">
                                      {userMap[msg.senderId]?.name?.charAt(0) || 'S'}
                                  </div>
                              )}
                              <div className={`max-w-xs md:max-w-md p-3 rounded-xl ${isCurrentUser ? 'bg-primary-500 text-white rounded-br-none' : 'bg-white text-secondary-800 rounded-bl-none border'}`}>
                                  {!isCurrentUser && <p className="text-xs font-bold mb-1 text-primary-600">{getSenderName(msg.senderId)}</p>}
                                  
                                  {msg.imageUrl && (
                                      <a href={msg.imageUrl} target="_blank" rel="noopener noreferrer">
                                        <img src={msg.imageUrl} alt="attachment" className="rounded-lg mb-2 max-h-48 cursor-pointer" />
                                      </a>
                                  )}
                                  
                                  {msg.text && <p className="text-sm break-words">{msg.text}</p>}

                                  <p className={`text-xs mt-1 opacity-70`}>{formatDate(msg.createdAt, { hour: '2-digit', minute: '2-digit' })}</p>
                              </div>
                          </div>
                      </div>
                  );
              })}
              <div ref={messagesEndRef} />
          </CardContent>
          <CardFooter className="flex-col items-start">
              {imagePreview && (
                  <div className="mb-2 p-2 border rounded-lg relative">
                      <img src={imagePreview} alt="Preview" className="h-20 w-20 object-cover rounded" />
                      <button onClick={removeImage} className="absolute top-1 right-1 bg-black/50 text-white rounded-full p-0.5">
                          <X size={14} />
                      </button>
                  </div>
              )}
              <form onSubmit={handleSubmit} className="flex w-full gap-2 items-center">
                  <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handleFileChange}
                      className="hidden"
                      id="chat-file-input"
                  />
                  <Button type="button" variant="outline" size="sm" onClick={() => fileInputRef.current?.click()}>
                      <Paperclip className="w-4 h-4" />
                  </Button>
                  <input
                      type="text"
                      value={messageText}
                      onChange={(e) => setMessageText(e.target.value)}
                      placeholder={t('type_a_message')}
                      className="flex-grow p-2 border border-secondary-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                      disabled={addMessageMutation.isPending}
                  />
                  <Button type="submit" disabled={addMessageMutation.isPending || (!messageText.trim() && !imageFile)} isLoading={addMessageMutation.isPending}>
                      <Send className="w-4 h-4" />
                  </Button>
              </form>
          </CardFooter>
      </Card>
  );
}
